import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  DataGridPremium,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  isAutogeneratedRow,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid-premium';
import { huHU } from '@mui/x-data-grid/locales';
import { datagridLangHunCustomized } from '../../../config/datagridLangHunCustomized';
import {
  Select,
  MenuItem,
  IconButton,
  Modal,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  Checkbox,
} from '@mui/material';
import { Delete, Save, KeyboardDoubleArrowDown } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';

import useParam from '../../../context/ParamContext';
import { ButtonUI, ColorCircleUi, TrendIcon } from '../../Interface';
import biService from '../../../service/bi.service';
import { BiFieldType } from '../../../config/bi_report';
import { getDataGridStyles } from '../../Interface/Datagrid/dataGridStyles';
import FeedbackDialog from './FeedbackDialog';
import { formatCurrency } from '../../../utils/currencyHelper';

const TableReport = ({
  report,
  height,
  setRefresh,
  linkedReportIdState,
  activeLevelState,
  filterValsState,
  reportStatesState,
}) => {
  const theme = useTheme();
  const arf = useGridApiRef();
  const { getParam, params } = useParam();
  const apiRef = useGridApiRef();

  const { activeLevel, setActiveLevel } = activeLevelState;
  const { filterVals, setFilterVals } = filterValsState;
  const { linkedReportId, setLinkedReportId } = linkedReportIdState;
  const { reportStates, setReportStates } = reportStatesState;

  const [gridData, setGridData] = useState({ rows: [], rowCount: 0 });
  const [columns, setColumns] = useState([]);
  const [savedStates, setSavedStates] = useState({});
  const [selectedState, setSelectedState] = useState('');
  const [isSelectOpen, setSelectOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [stateName, setStateName] = useState('');
  const [isOverwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stateToDelete, setStateToDelete] = useState('');
  const [dialogData, setDialogData] = useState({});
  const [hasAppliedDefaultState, setHasAppliedDefaultState] = useState(false);

  const query = useMemo(
    () => ({
      filter: filterVals.filter,
      sorting: {
        sortModel: [],
      },
      pagination: {
        paginationModel: {
          page: 0,
        },
      },
    }),
    [report.id, filterVals]
  );

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: [],
        aggregation: {
          model: {},
        },
      },
      density: 'compact',
    },
  });

  const getTableData = useCallback(() => {
    biService.getReportData(report.id, null, query).then((data) => setGridData(data));
  }, [report.id, query]);

  const NumberField = ({ data, field }) => {
    const val = parseFloat(data.row?.[field?.field]);
    const currencyVal = formatCurrency(val);
    const threshold = {
      min: field.thresholdMin ? parseFloat(field.thresholdMin) : null,
      max: field.thresholdMax ? parseFloat(field.thresholdMax) : null,
      arrow: field.thresholdArrow,
    };
    const biTableFeedback = JSON.parse(params?.BI_TABLE_FEEDBACK ?? '[]');
    const hasFeedback = biTableFeedback.includes(report.id);

    return ((field.thresholdMin !== null && !isNaN(field.thresholdMin)) ||
      (field.thresholdMax !== null && !isNaN(field.thresholdMax))) &&
      !isNaN(val) ? (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '-3px' }}>
        <Box>{field.type === BiFieldType.CURRENCY ? currencyVal : val}</Box>
        <TrendIcon
          rowData={data}
          field={field}
          threshold={threshold}
          hasFeedback={hasFeedback}
          setDialogData={setDialogData}
        />
      </Box>
    ) : !isNaN(val) ? (
      field.type === BiFieldType.CURRENCY ? (
        currencyVal
      ) : (
        val
      )
    ) : (
      data.row?.[field?.field]
    );
  };

  const onDrillClick = (field, value) => {
    const { biDrillingLink, biDrilling, id } = report;

    const maxLevel = biDrilling.length > 0 ? Math.max(...biDrilling.map((drill) => drill.level)) : null;
    let actLevel = activeLevel !== null ? activeLevel + 1 : 0;

    const newFilter = { ...filterVals };

    if (maxLevel + 1 === actLevel || (biDrillingLink && maxLevel === null)) {
      if (biDrillingLink) {
        setReportStates((prev) => {
          prev.push({
            reportId: id,
            linkedReportId: linkedReportId,
            filter: { filter: newFilter.filter.map((data) => data) },
            activeLevel,
          });
          return prev;
        });
        setLinkedReportId(biDrillingLink.linkedReport);

        newFilter.filter.push({ field, value });

        const updatedFilter = newFilter.filter.map((item) => {
          const newReportField = biDrillingLink.biDrillingLinkField.find(
            (linkField) => linkField.parentField === item.field
          );
          if (newReportField) {
            return { ...item, field: newReportField.childField };
          }
          return item;
        });

        setFilterVals({ filter: updatedFilter });
        setActiveLevel(null);
      } else {
        return;
      }
    } else {
      newFilter.filter.push({ field, value });
      if (!biDrilling.length > 0) {
        return;
      }
      setActiveLevel(actLevel);
      setFilterVals(newFilter);
    }
  };

  const generateColumns = () => {
    const col = [];

    for (const field of report.biField) {
      if (field.type !== BiFieldType.HIDDEN) {
        const column = {
          field: field?.field,
          headerName: field?.name,
          headerClassName: 'bg-tableHeaderColor',
          filterable: true,
          sortable: true,
          flex: 1,
          minWidth: 100,
        };

        switch (field.type) {
          case BiFieldType.DATE:
            column.type = 'date';
            column.valueGetter = (value) => (value ? new Date(value) : null);
            column.renderCell = (value) => {
              if (!isAutogeneratedRow(value.row)) {
                return value.row?.[field?.field] ? dayjs(value.row?.[field?.field]).format('YYYY.MM.DD') : '';
              } else {
                return value.formattedValue ? dayjs(value.formattedValue).format('YYYY.MM.DD') : '';
              }
            };

            break;
          case BiFieldType.DATETIME:
            column.type = 'dateTime';
            column.valueGetter = (value) => (value ? new Date(value) : null);
            column.renderCell = (value) => {
              if (!isAutogeneratedRow(value.row)) {
                return value.row?.[field?.field] ? dayjs(value.row?.[field?.field]).format('YYYY.MM.DD HH:mm') : '';
              } else {
                return value.formattedValue ? dayjs(value.formattedValue).format('YYYY.MM.DD HH:mm') : '';
              }
            };
            break;
          case BiFieldType.TIME:
            column.valueFormatter = (data) => (data ? dayjs(data).format('HH:mm') : '');
            break;
          case BiFieldType.NUMBER:
          case BiFieldType.CURRENCY:
            column.type = 'number';
            column.valueGetter = (data) => {
              return data ? parseFloat(data) : '';
            };
            column.renderCell = (data) => {
              if (!isAutogeneratedRow(data.row)) {
                return <NumberField data={data} field={field} />;
              } else {
                return data.formattedValue;
              }
            };
            break;
          case BiFieldType.COLOR:
            column.sortable = false;
            column.filterable = false;
            column.renderCell = (data) => {
              const colorValue = data.row?.[field?.field];
              return <ColorCircleUi color={colorValue} size={20} />;
            };
            break;
          default:
            break;
        }

        col.push(column);

        if (field.isGroup) {
          const drillColumn = {
            field: `__bi_drill_${field.field}`,
            headerName: `Fúrás`,
            headerClassName: 'bg-tableHeaderColor',
            filterable: false,
            sortable: false,
            flex: 0.8,
            minWidth: 80,
            renderCell: (data) => {
              if (!isAutogeneratedRow(data.row)) {
                return (
                  <IconButton
                    aria-label="save"
                    size="small"
                    onClick={() => {
                      onDrillClick(field.field, data.row[field.field]);
                    }}
                    sx={{
                      color: 'primary.main',
                    }}
                  >
                    <KeyboardDoubleArrowDown />
                  </IconButton>
                );
              } else {
                return data.formattedValue;
              }
            },
          };
          col.push(drillColumn);
        }
      }
    }

    setColumns(col);
  };

  useEffect(() => {
    if (!Object.keys(params).length) {
      getParam();
    }
  }, []);

  useEffect(() => {
    if (Object.keys(report).length) {
      generateColumns();
    }
  }, [report, report?.biField]);

  useEffect(() => {
    const prevReport = reportStates[reportStates.length - 1];
    if (Object.keys(query).length > 0 && prevReport?.reportId !== report.id) {
      getTableData();
    }
  }, [query]);

  useEffect(() => {
    biService.getReportReportGridState(report.id).then((savedStateTable) => setSavedStates(savedStateTable));
    // const savedStates = JSON.parse(localStorage.getItem(`biGridStates_${report.id}`)) || {};
    // setSavedStates(savedStates);
  }, [report.id]);

  useEffect(() => {
    if (params) {
    }
  }, [params]);

  const handleSaveState = () => {
    setModalOpen(true);
  };

  const handleSaveStateConfirm = () => {
    if (savedStates[stateName]) {
      setModalOpen(false);
      setOverwriteDialogOpen(true);
    } else {
      saveState();
    }
  };

  const saveState = async () => {
    const rawCurrentState = apiRef.current.exportState();
    const { rows: _rows, ...currentState } = rawCurrentState || {};

    const previousState = savedStates[stateName];
    const isDefault =
      previousState && typeof previousState === 'object' && 'isDefault' in previousState
        ? previousState.isDefault
        : false;

    const newSavedStates = {
      ...savedStates,
      [stateName]: { ...currentState, isDefault },
    };

    setSavedStates(newSavedStates);
    // localStorage.setItem(`biGridStates_${report.id}`, JSON.stringify(newSavedStates));
    await biService.saveReportGridState(report.id, newSavedStates);
    setModalOpen(false);
    setOverwriteDialogOpen(false);
    setStateName('');
  };

  const handleConfirmOverwrite = () => {
    saveState();
  };

  const handleLoadState = useCallback(
    (stateName) => {
      const savedState = savedStates[stateName];
      if (savedState) {
        const { rows: _rows, isDefault: _isDefault, ...gridState } = savedState || {};
        apiRef.current.restoreState(gridState);
      }
    },
    [savedStates, apiRef]
  );

  const setDefaultState = async (stateName) => {
    const updatedStates = Object.entries(savedStates).reduce((acc, [name, stateValue]) => {
      const baseState = stateValue && typeof stateValue === 'object' ? stateValue : {};
      const { rows: _rows, ...cleanState } = baseState;
      acc[name] = { ...cleanState, isDefault: stateName ? name === stateName : false };
      return acc;
    }, {});
    setSavedStates(updatedStates);
    await biService.saveReportGridState(report.id, updatedStates);
  };

  const handleDeleteState = (stateName) => {
    setStateToDelete(stateName);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteState = async () => {
    const newSavedStates = { ...savedStates };
    delete newSavedStates[stateToDelete];
    setSavedStates(newSavedStates);
    // localStorage.setItem(`biGridStates_${report.id}`, JSON.stringify(newSavedStates));
    await biService.saveReportGridState(report.id, newSavedStates);
    setDeleteDialogOpen(false);
    setStateToDelete('');
    if (selectedState === stateToDelete) {
      setSelectedState('');
    }
  };

  useEffect(() => {
    if (selectedState) {
      handleLoadState(selectedState);
    }
  }, [selectedState, handleLoadState]);

  useEffect(() => {
    if (hasAppliedDefaultState) return;
    if (!savedStates || Object.keys(savedStates).length === 0) return;

    const defaultEntry = Object.entries(savedStates).find(([, stateValue]) => stateValue?.isDefault);
    if (defaultEntry) {
      const [defaultName] = defaultEntry;
      setSelectedState(defaultName);
    }
    setHasAppliedDefaultState(true);
  }, [savedStates, hasAppliedDefaultState, setSelectedState]);

  const CustomToolbar = () => {
    const exportFileName = `${report?.name || 'report'} ${dayjs().format('YYMMDD_HHmm')}`;

    return (
      <GridToolbarContainer>
        <FormControl variant="standard" sx={{ width: '200px' }}>
          <Select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            displayEmpty
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            onClick={() => setSelectOpen(true)}
            sx={{
              color: 'primary.main',
              '& .MuiSelect-icon': {
                color: 'primary.main',
              },
            }}
          >
            <MenuItem value="" disabled>
              Mentett állapot kiválasztása
            </MenuItem>
            {Object.keys(savedStates)
              .sort()
              .map((state) => (
                <MenuItem key={state} value={state}>
                  <Checkbox
                    size="small"
                    checked={!!savedStates[state]?.isDefault}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setDefaultState(state);
                      } else {
                        if (savedStates[state]?.isDefault) {
                          setDefaultState(null);
                        }
                      }
                    }}
                    sx={{ p: 0, mr: 1 }}
                  />
                  {state}
                  {isSelectOpen && (
                    <IconButton
                      aria-label="delete"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteState(state);
                      }}
                      sx={{
                        marginLeft: 'auto',
                        color: 'primary.main',
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <IconButton
          aria-label="save"
          size="medium"
          onClick={handleSaveState}
          sx={{
            color: 'primary.main',
          }}
        >
          <Save />
        </IconButton>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport
          csvOptions={{ fileName: exportFileName }}
          excelOptions={{ fileName: exportFileName }}
          printOptions={{ fileName: exportFileName }}
        />
      </GridToolbarContainer>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-2 p-4">
      <div className="col-span-1 flex items-center justify-center text-labelColor text-xl w-full">
        <p>{report.name}</p>
      </div>

      <div className="tableReport">
        <DataGridPremium
          initialState={initialState}
          apiRef={apiRef}
          rows={gridData.rows}
          columns={columns}
          groupingColDef={{
            hideable: false,
          }}
          pagination
          rowHeight={45}
          sx={getDataGridStyles(theme, { height: height ? height - 50 : 550 })}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            columnsPanel: {
              sx: {
                '& .MuiDataGrid-panelFooter button:first-child': {
                  display: 'none',
                },
              },
            },
          }}
          localeText={{ ...huHU.components.MuiDataGrid.defaultProps.localeText, ...datagridLangHunCustomized }}
          disableSelectionOnClick
          headerFilters
        />
      </div>
      {/* Save State Modal */}
      <Modal open={isModalOpen} onClose={() => setModalOpen(false)}>
        <div
          className="modal-container"
          style={{
            padding: 20,
            backgroundColor: 'white',
            margin: 'auto',
            top: '20%',
            position: 'relative',
            width: 300,
          }}
        >
          <h2>Jelenlegi állapot mentése</h2>
          <TextField label="State Name" value={stateName} onChange={(e) => setStateName(e.target.value)} fullWidth />
          <Button variant="contained" onClick={handleSaveStateConfirm} disabled={!stateName} style={{ marginTop: 10 }}>
            Mentés
          </Button>
        </div>
      </Modal>

      {/* Overwrite Confirmation Dialog */}
      <Dialog open={isOverwriteDialogOpen} onClose={() => setOverwriteDialogOpen(false)}>
        <DialogTitle>Felülírás megerősítése</DialogTitle>
        <DialogContent>
          <DialogContentText>Már létezik egy állapot "{stateName}" névvel. Szeretné felülírni?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverwriteDialogOpen(false)}>Mégsem</Button>
          <Button onClick={handleConfirmOverwrite} color="primary" variant="contained">
            Felülír
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Törlés megerősítése</DialogTitle>
        <DialogContent>
          <DialogContentText>Biztos benne, hogy törölni szeretné a(z) "{stateToDelete}" állapotot?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <ButtonUI text={'Törlés'} className="bg-danger" type="button" onClick={confirmDeleteState} />
        </DialogActions>
      </Dialog>

      <FeedbackDialog
        show={Object.keys(dialogData).length > 0}
        onHide={() => {
          setDialogData({});
        }}
        selectedValues={dialogData}
        biReportId={report.id}
        setRefresh={setRefresh}
      />
    </div>
  );
};

export default TableReport;
